import { ReactElement, useMemo, useState } from 'react'
import { Button, Box } from '@mui/material'
import { format } from 'date-fns'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'

import Breadcrumb from 'src/components/shared/Breadcrumb/Breadcrumb'
import EnhancedTable from 'src/components/shared/EnhancedTable/EnhancedTable'
import { StatelessPdfDialog } from 'src/components/shared/PdfDialog'
import { PDFIcon } from 'src/components/shared/ui/Icons'
import MainContentPaper from 'src/components/shared/ui/MainContentPaper'
import TextWithIcon from 'src/components/shared/ui/TextWithIcon'
import ToolTipEllipsis from 'src/components/shared/ui/TooltipEllipsis'
import { AvisoLexnet } from 'src/interfaces'

import { avisosLexnetStyles as styles } from './AvisosLexnetStyles'

type Props = {
  avisosLexnet: AvisoLexnet[]
}

function AvisosLexnetView({ avisosLexnet }: Props): ReactElement {
  const [selectedAdjunto, setSelectedAjunto] = useState<Record<string, any> | null>(null)
  const { t } = useTranslation()

  function clearSelectedAdjunto() {
    setSelectedAjunto(null)
  }

  const columns = useMemo(
    () => [
      {
        Header: 'Fecha',
        accessor: 'createdAt',
        width: 100,
        Cell: ({ value }) => format(new Date(value), 'dd/MM/yyyy')
      },
      {
        Header: 'Descripción',
        accessor: (avisoLexnet: AvisoLexnet) => avisoLexnet.texto?.toLocaleLowerCase(),
        Cell: ({ row, data }) => data[row.index].texto,
        width: 150
      },
      {
        Header: 'Importancia',
        accessor: 'importancia',
        Cell: ({ value }) => (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={styles.cellImportancia(value.toLowerCase())}
          >
            <ToolTipEllipsis text={value && value[0].toUpperCase() + value.slice(1)}>
              <span>{value}</span>
            </ToolTipEllipsis>
          </Box>
        ),
        width: 75
      },
      {
        Header: 'Parada',
        accessor: (avisoLexnet: AvisoLexnet) => (avisoLexnet.parada ? 'Si' : 'No'),
        Cell: ({ value }) => (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={styles.cellParada(value === 'Si')}
          >
            {value}
          </Box>
        ),
        width: 75
      },
      {
        Header: 'Fecha parada',
        accessor: 'fechaParada',
        width: 100,
        Cell: ({ value }) => (value ? format(new Date(value), 'dd/MM/yyyy') : '')
      },
      {
        Header: 'Duración parada',
        accessor: 'duracionParada',
        Cell: ({ value }) => (value ? `${value} horas` : ''),
        width: 100
      },

      {
        Header: 'Documento adjunto',
        accessor: (avisoLexnet: AvisoLexnet) => avisoLexnet.nombreAdjunto?.toLocaleLowerCase(),
        Cell: ({ row, data }) => {
          const { adjunto, nombreAdjunto } = data[row.index]

          return adjunto ? (
            <Button
              sx={styles.btnDocument}
              onClick={() =>
                setSelectedAjunto({
                  uri: adjunto,
                  name: nombreAdjunto
                })
              }
            >
              <TextWithIcon icon={<PDFIcon sx={styles.documentIcon} />} sx={styles.document}>
                {nombreAdjunto}
              </TextWithIcon>
            </Button>
          ) : null
        },
        width: 100
      }
    ],
    []
  )

  return (
    <>
      <Helmet>
        <title>Avisos LexNET</title>
      </Helmet>
      <Breadcrumb section="Avisos de LexNET" />
      <MainContentPaper>
        <EnhancedTable
          columns={columns}
          data={avisosLexnet}
          noRegistersMessage={t('components.enhanced_table.no_registers.default')}
        />
      </MainContentPaper>
      <StatelessPdfDialog
        onClose={clearSelectedAdjunto}
        isOpened={Boolean(selectedAdjunto)}
        documentUri={selectedAdjunto && selectedAdjunto.uri}
        filename={selectedAdjunto && selectedAdjunto.name}
      />
    </>
  )
}

export default AvisosLexnetView
